import { isEqual } from 'lodash-es';
import { DateTime } from 'luxon';
import { API_DATE_FORMAT } from '~/constants/_constants';
import browserStorage from '~/plugins/browserStorage';
import { DeepPartial, DeepPartialObject } from '~/utils/_types';
import { defaultErrorMap, string, z } from 'zod';
import { CategoryItemDetail, Price } from '~/api/links/_types';
import { zNullishDefault } from '~/plugins/zod/utils';
import { DemandFormData } from '~/api/demands/_types';

// @plachtova: objekt vhodny pro cache do browser storage
// data z browser storage timto muzeme jednoduse zvalidovat, jinak je zahodime
const DemandDraftCachedDetailSchema = z.object({
    Id: z.number().nullish(),
    ReferenceNumber: z.string().nullish(),
    RequiredAt: string().nullish(),
    Address: z.string().nullish(),
    Note: z.string().nullish(),
    Lines: zNullishDefault(
        z
            .object({
                Id: z.string(),
                // pouze pro FE, custom schema bude skipovat validaci pro tuto property
                Item: z.custom<CategoryItemDetail>(),
                Quantity: z.number().min(1),
                RequiredAt: string().nullish(),
                Configuration: z
                    .object({
                        RuleId: z.number(),
                        OptionId: z.number(),
                    })
                    .array(),
                CategoryId: z.number().nullish(),
                Nomenclature: z.custom<string | null>(),
                Price: z.custom<Price | null>(),
            })
            .array(),
        []
    ),
});

type DemandDraftCachedDetail = z.infer<typeof DemandDraftCachedDetailSchema>;

export const useDemandCartStore = defineStore('demand-cart', () => {
    const STORAGE_KEY = 'demand-cart-values';

    const formValues = ref<DeepPartial<DemandFormData>>();
    const loadDemandCart = () => {
        let storageValues: DeepPartialObject<DemandDraftCachedDetail> | null = null;

        try {
            storageValues = JSON.parse(browserStorage.get(STORAGE_KEY));
            if (storageValues == null) return;

            storageValues = DemandDraftCachedDetailSchema.parse(storageValues, { errorMap: defaultErrorMap });
        } catch (err) {
            console.error(`Browser storage demand cart values zod transform error:`);
            console.error(`Original browser Data: `, storageValues);
            console.error(`Zod Error: `, err);

            return;
        }

        const newValues = {
            ...storageValues,
            RequiredAt:
                storageValues.RequiredAt == null
                    ? null
                    : DateTime.fromFormat(storageValues.RequiredAt, API_DATE_FORMAT),
            Lines: storageValues.Lines?.map((x) => ({
                ...x,
                RequiredAt: x.RequiredAt == null ? null : DateTime.fromFormat(x.RequiredAt, API_DATE_FORMAT),
            })),
        };

        if (isEqual(newValues, formValues.value)) return;

        formValues.value = newValues;
    };
    const setDemandCart = (values: DeepPartial<DemandFormData>) => {
        formValues.value = values;
        const storageValues = {
            ...values,
            RequiredAt: (values.RequiredAt as DateTime<boolean> | null | undefined)?.toFormat(API_DATE_FORMAT),
            Lines: values.Lines?.map((x) => ({
                ...x,
                RequiredAt: (x.RequiredAt as DateTime<boolean> | null | undefined)?.toFormat(API_DATE_FORMAT),
            })),
        };

        browserStorage.set(STORAGE_KEY, JSON.stringify(storageValues));
    };
    const reset = () => {
        formValues.value = undefined;
        browserStorage.remove(STORAGE_KEY);
    };

    return {
        formValues,
        loadDemandCart,
        setDemandCart,
        reset,
    };
});
